import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import {
  createAgencyRoleType,
  deleteAgencyRoleType,
  fetchAgencies,
  fetchAgencyMemberships,
  fetchAgencyRoleTypes,
  fetchAllowedCertifications,
  updateAgencyRoleType,
} from "../../api/apiClient";
import { canPickAgency, defaultAgencyId } from "./agencySelection";
import { useTenant } from "../../contexts/TenantContext";
import { useActiveTenant } from "../../hooks/useActiveTenant";
import NoTenantSelected from "../../components/NoTenantSelected";

function errorMessage(e) {
  return e?.response?.data?.message || "Something went wrong. Try again.";
}

export default function AgencyRoleRequirements() {
  const { user, isFeatureEnabled } = useTenant();
  const { tenantId: activeTenantId } = useActiveTenant();
  const navigate = useNavigate();

  const [agencies, setAgencies] = useState([]);
  const [selectedAgencyId, setSelectedAgencyId] = useState(null);
  const [memberships, setMemberships] = useState([]);
  const [roleTypes, setRoleTypes] = useState([]);
  const [allowedCerts, setAllowedCerts] = useState([]);
  const [loading, setLoading] = useState(false);

  const [editTarget, setEditTarget] = useState(null);
  const [editName, setEditName] = useState("");
  const [editCertIds, setEditCertIds] = useState([]);

  const [newOpen, setNewOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [newCertIds, setNewCertIds] = useState([]);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [dialogError, setDialogError] = useState("");

  const agencyManagementEnabled = isFeatureEnabled("agencyManagement");

  useEffect(() => {
    if (!activeTenantId) return;
    fetchAgencies(activeTenantId).then((list) => {
      const arr = Array.isArray(list) ? list : list?.content || [];
      setAgencies(arr);
      const initial = defaultAgencyId(arr, user);
      if (initial) {
        setSelectedAgencyId(initial);
      } else {
        setLoading(false);
      }
    });
    fetchAllowedCertifications(activeTenantId).then(setAllowedCerts);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTenantId]);

  const reload = useCallback(async () => {
    if (!activeTenantId || !selectedAgencyId) return;
    setLoading(true);
    try {
      const [mems, types] = await Promise.all([
        fetchAgencyMemberships(activeTenantId, selectedAgencyId),
        fetchAgencyRoleTypes(activeTenantId, selectedAgencyId),
      ]);
      setMemberships(mems.filter((m) => m.status === "APPROVED" && m.user));
      setRoleTypes(types);
    } finally {
      setLoading(false);
    }
  }, [activeTenantId, selectedAgencyId]);

  useEffect(() => {
    if (selectedAgencyId) reload();
  }, [selectedAgencyId, reload]);

  const rows = roleTypes.map((rt) => ({
    ...rt,
    members: memberships
      .filter((m) => m.category?.toLowerCase() === rt.name.toLowerCase())
      .map((m) => m.user),
  }));
  const typeNames = new Set(roleTypes.map((rt) => rt.name.toLowerCase()));
  const unassigned = memberships
    .filter((m) => !m.category || !typeNames.has(m.category.toLowerCase()))
    .map((m) => m.user);

  function certName(id) {
    const ct = allowedCerts.find((c) => c.id === id);
    return ct ? ct.name : id;
  }

  function openNew() {
    setDialogError("");
    setNewName("");
    setNewCertIds([]);
    setNewOpen(true);
  }

  function openEdit(rt) {
    setDialogError("");
    setEditTarget(rt);
    setEditName(rt.name);
    setEditCertIds(rt.requiredCertIds || []);
  }

  function openDelete(rt) {
    setDialogError("");
    setDeleteTarget(rt);
  }

  async function saveNew() {
    try {
      await createAgencyRoleType(activeTenantId, selectedAgencyId, {
        name: newName.trim(),
        requiredCertIds: newCertIds,
      });
      setNewOpen(false);
      reload();
    } catch (e) {
      setDialogError(errorMessage(e));
    }
  }

  async function saveEdit() {
    try {
      await updateAgencyRoleType(activeTenantId, selectedAgencyId, editTarget.id, {
        name: editName.trim(),
        requiredCertIds: editCertIds,
      });
      setEditTarget(null);
      reload();
    } catch (e) {
      setDialogError(errorMessage(e));
    }
  }

  async function confirmDelete() {
    try {
      await deleteAgencyRoleType(activeTenantId, selectedAgencyId, deleteTarget.id);
      setDeleteTarget(null);
      reload();
    } catch (e) {
      setDialogError(errorMessage(e));
    }
  }

  function renderMembers(members) {
    return (
      <Stack direction="row" flexWrap="wrap" gap={0.5}>
        {members.map((m) => (
          <Chip
            key={m.id}
            label={m.fullName || m.name || m.username}
            size="small"
            variant="outlined"
          />
        ))}
      </Stack>
    );
  }

  function renderCerts(ids = []) {
    if (ids.length === 0) {
      return (
        <Typography variant="caption" color="text.secondary">
          None set
        </Typography>
      );
    }
    return (
      <Stack direction="row" flexWrap="wrap" gap={0.5}>
        {ids.map((id) => (
          <Chip
            key={id}
            label={certName(id)}
            size="small"
            color="primary"
            variant="outlined"
          />
        ))}
      </Stack>
    );
  }

  function certSelect(value, onChange) {
    return (
      <FormControl fullWidth>
        <InputLabel>Required Certifications</InputLabel>
        <Select
          multiple
          value={value}
          onChange={(e) => onChange(e.target.value)}
          input={<OutlinedInput label="Required Certifications" />}
          renderValue={(selected) =>
            selected.length === 0 ? "None" : selected.map(certName).join(", ")
          }
        >
          {allowedCerts.map((ct) => (
            <MenuItem key={ct.id} value={ct.id}>
              {ct.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  }

  const errorAlert = dialogError && (
    <Alert severity="error" sx={{ mb: 2 }}>
      {dialogError}
    </Alert>
  );

  if (!agencyManagementEnabled) {
    return (
      <Container maxWidth="xl" sx={{ pb: 4 }}>
        <Card>
          <CardContent>
            <Alert severity="info">
              Agency management is not enabled for your tenant.
            </Alert>
          </CardContent>
        </Card>
      </Container>
    );
  }

  if (
    !user ||
    !user.roles?.some((r) =>
      ["AGENCY_ADMIN", "GLOBAL_ADMIN", "ADMIN"].includes(r),
    )
  ) {
    return (
      <Container maxWidth="xl" sx={{ pb: 4 }}>
        <Card>
          <CardContent>
            <Alert severity="error">
              You do not have permission to view this page.
            </Alert>
          </CardContent>
        </Card>
      </Container>
    );
  }

  if (!activeTenantId) {
    return (
      <Container maxWidth="xl" sx={{ pb: 4 }}>
        <NoTenantSelected message="Select a training center from the top bar to manage role requirements." />
      </Container>
    );
  }

  return (
    <>
      {/* ── Header ── */}
      <Box
        sx={{
          backgroundColor: "background.paper",
          borderBottom: "1px solid",
          borderColor: "grey.200",
          mb: 3,
        }}
      >
        <Container maxWidth="xl" sx={{ py: { xs: 3, md: 4 } }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/agency")}
            sx={{
              color: "text.secondary",
              mb: 2,
              textTransform: "none",
              fontWeight: 500,
            }}
          >
            Back to Agency Management
          </Button>
          <Typography
            variant="h4"
            sx={{ fontWeight: 600, mb: 1, fontFamily: "var(--font-display)" }}
          >
            Role Requirements
          </Typography>
          <Typography color="text.secondary">
            Manage certification requirements per member category / role
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="xl" sx={{ pb: 4 }}>
        {canPickAgency(user) && (
          <>
            {agencies.length > 1 && (
              <FormControl size="small" sx={{ mb: 3, minWidth: 260 }}>
                <InputLabel>Agency</InputLabel>
                <Select
                  value={selectedAgencyId || ""}
                  label="Agency"
                  onChange={(e) => setSelectedAgencyId(e.target.value)}
                >
                  {agencies.map((a) => (
                    <MenuItem key={a.id} value={a.id}>
                      {a.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            {agencies.length === 1 && (
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 3 }}>
                {agencies[0].name}
              </Typography>
            )}
          </>
        )}

        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
          <Button
            variant="outlined"
            startIcon={<AddCircleIcon />}
            onClick={openNew}
          >
            Add Role / Category
          </Button>
        </Box>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : rows.length === 0 && unassigned.length === 0 ? (
          <Paper sx={{ p: 3, textAlign: "center" }}>
            <Typography color="text.secondary">
              No roles yet. Add a role to define its required certifications.
            </Typography>
          </Paper>
        ) : (
          <TableContainer component={Card}>
            <Table size="small">
              <TableHead>
                <TableRow
                  sx={{
                    "& th": { fontWeight: "bold", bgcolor: "action.hover" },
                  }}
                >
                  <TableCell>Category / Role</TableCell>
                  <TableCell>Members</TableCell>
                  <TableCell>Required Certifications</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((rt) => (
                  <TableRow
                    key={rt.id}
                    hover
                    data-testid={`role-type-row-${rt.name}`}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {rt.name}
                      </Typography>
                    </TableCell>
                    <TableCell>{renderMembers(rt.members)}</TableCell>
                    <TableCell>{renderCerts(rt.requiredCertIds)}</TableCell>
                    <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
                      <Button
                        size="small"
                        startIcon={<EditIcon />}
                        onClick={() => openEdit(rt)}
                        data-testid="role-type-edit"
                      >
                        Edit
                      </Button>
                      <Tooltip
                        title={
                          rt.members.length > 0
                            ? "Reassign this role's members before deleting it"
                            : "Delete role"
                        }
                      >
                        <span>
                          <Button
                            size="small"
                            color="error"
                            startIcon={<DeleteIcon />}
                            disabled={rt.members.length > 0}
                            onClick={() => openDelete(rt)}
                            data-testid="role-type-delete"
                          >
                            Delete
                          </Button>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                {unassigned.length > 0 && (
                  <TableRow>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        (No Category)
                      </Typography>
                    </TableCell>
                    <TableCell>{renderMembers(unassigned)}</TableCell>
                    <TableCell />
                    <TableCell />
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Container>

      <Dialog
        open={!!editTarget}
        onClose={() => setEditTarget(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontFamily: "var(--font-display)" }}>
          Edit "{editTarget?.name}"
        </DialogTitle>
        <DialogContent>
          {errorAlert}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Changes apply to every member with this role.
          </Typography>
          <TextField
            label="Category / Role Name"
            fullWidth
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            inputProps={{ maxLength: 100, "data-testid": "role-type-name-input" }}
            sx={{ mb: 2, mt: 1 }}
          />
          {certSelect(editCertIds, setEditCertIds)}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditTarget(null)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!editName.trim()}
            onClick={saveEdit}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={newOpen}
        onClose={() => setNewOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontFamily: "var(--font-display)" }}>
          Add Role / Category
        </DialogTitle>
        <DialogContent>
          {errorAlert}
          <Alert severity="info" sx={{ mb: 2 }}>
            Define a new role. Members get this role when you approve them in
            Agency Management.
          </Alert>
          <TextField
            label="Category / Role Name"
            fullWidth
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            inputProps={{ maxLength: 100, "data-testid": "role-type-name-input" }}
            sx={{ mb: 2 }}
          />
          {certSelect(newCertIds, setNewCertIds)}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!newName.trim()}
            onClick={saveNew}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontFamily: "var(--font-display)" }}>
          Delete "{deleteTarget?.name}"?
        </DialogTitle>
        <DialogContent>
          {errorAlert}
          <Typography variant="body2">
            This removes the role and its certification requirements.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={confirmDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
